import * as Sentry from "@sentry/vue";

import { defineStore, storeToRefs } from "pinia";
import { useToast } from "primevue/usetoast";
import { onMounted, ref, watch } from "vue";
import { useRoute } from "vue-router";

import { useStekManager } from "@/composables/stek-manager";
import { syncChannel } from "@/lib/broadcast-channels";
import { api, type types } from "@/lib/openapi";
import { onEntityChange } from "@/stores/cache-invalidation";
import { type DomainInTree } from "@/stores/domains-store";
import { type ProcessInTree } from "@/stores/processes-store";
import { type ProductInTree } from "@/stores/products-store";
import { useReleaseStore } from "@/stores/release-store";

export type Worktree = types.Worktree;

// EntityType string values (from the OpenAPI enum) whose data is embedded in
// the worktree tree; a change to any of them must refresh it (lists, ordering,
// relationships, …). Matched on the stable wire values rather than importing
// the EntityType enum, which would create an import cycle via entity-store.
const WORKTREE_ENTITY_TYPES = new Set<string>([
    "domain",
    "product",
    "process",
    "role",
    "business_entity",
]);

// The worktree store is primarily used for the lefthand stekz-tree component.
// However, it stores quite a bit of information which is useful for other pages
// that want to use some meta info (i.e. what is the current domain identity)
// without having those pages to actually fetch the entire entity from the api.
export const useWorktreeStore = defineStore("worktree", () => {
    const worktree = ref<Worktree>();
    const isFetching = ref(false);
    const { stek } = useStekManager();
    const toast = useToast();
    const route = useRoute();

    // Route id param -> EntityType wire value. When viewing any of these at a
    // historical version, the tree rewinds that entity's OWNING domain subtree
    // to the revision (the SHA is a commit in that domain's repo only); other
    // domains stay current. The backend resolves the owning domain from the
    // entity, so process/role/business_entity/etc. work without the domain id
    // being in their (flat) route.
    const ENTITY_PARAM_TO_TYPE: Record<string, string> = {
        domainId: "domain",
        productId: "product",
        processId: "process",
        roleId: "role",
        businessEntityId: "business_entity",
        domainTermId: "domain_term",
    };

    const historicalEntityScope = (): {
        revision: string;
        entityType: string;
        entityId: string;
    } | null => {
        // `route` is undefined when the store is used outside a router context
        // (e.g. unit tests that don't install vue-router) — treat as no version.
        const version = route?.params?.version;
        if (typeof version !== "string" || !version || version === "current") {
            return null;
        }
        for (const [param, entityType] of Object.entries(
            ENTITY_PARAM_TO_TYPE
        )) {
            const id = route.params[param];
            if (typeof id === "string" && id) {
                return { revision: version, entityType, entityId: id };
            }
        }
        return null;
    };

    const controller = ref(new AbortController());

    const handleSyncError = (error: unknown, context: string) => {
        const errorMessage =
            error instanceof Error
                ? error.message
                : typeof error === "string"
                  ? error
                  : "Failed to sync worktree";

        toast.add({
            severity: "error",
            summary: "Sync Error",
            detail: `Failed to load ${context}: ${errorMessage}`,
        });
        Sentry.captureException(error);
        if (context !== "tree of the stek") {
            console.error("Error syncing worktree:", error);
        }
        isFetching.value = false;
    };

    const sync = async () => {
        // Abort any previously running requests and create a new controller
        controller.value.abort();
        controller.value = new AbortController();

        isFetching.value = true;
        try {
            const historical = historicalEntityScope();
            const revision = historical
                ? historical.revision
                : (useReleaseStore().activeRevision ?? undefined);
            const { response, data } =
                await api.startWorkspaceWorktreeMgtGetWorktreeStartPost({
                    body: {
                        workspace_id: "default",
                        revision,
                        revision_entity_type: historical?.entityType,
                        revision_entity_id: historical?.entityId,
                    },
                    signal: controller.value.signal,
                });

            if (!response.ok) {
                handleSyncError(
                    response.statusText || "Failed to sync worktree",
                    "tree of the stek"
                );
                return;
            }

            worktree.value = data;
        } catch (error) {
            if (error instanceof DOMException && error.name === "AbortError") {
                // Request was aborted by the AbortController.
                return;
            } else {
                handleSyncError(error, "workspace structure");
            }
        } finally {
            isFetching.value = false;
        }
    };

    watch(
        () => stek.value?.identity,
        () => {
            if (stek.value) {
                sync();
            } else {
                worktree.value = undefined;
            }
        },
        { immediate: true }
    );

    function findDomainByIdentity(identity: string): DomainInTree | undefined {
        if (!worktree.value) {
            return;
        }

        for (const domain of worktree.value.domains) {
            if (domain.identity === identity) {
                return domain;
            }
        }
    }

    function findProductByIdentity(
        identity: string
    ): ProductInTree | undefined {
        if (!worktree.value) {
            return;
        }

        for (const domain of worktree.value.domains) {
            for (const product of domain.products) {
                if (product.identity === identity) {
                    return product;
                }
            }
        }
    }

    function findProcessByIdentity(
        identity: string
    ): ProcessInTree | undefined {
        if (!worktree.value) {
            return;
        }

        for (const domain of worktree.value.domains) {
            for (const product of domain.products) {
                for (const process of product.processes) {
                    if (process.identity === identity) {
                        return process;
                    }
                }
            }
        }
    }

    onMounted(() => {
        const releaseStore = useReleaseStore();
        const { sync } = useWorktreeStore();

        // Re-sync when active release/revision changes
        watch(
            () => releaseStore.activeRevision,
            () => {
                if (stek.value) {
                    sync();
                }
            }
        );

        // Re-sync when the historical scope changes — entering/leaving a version,
        // or moving between entities/versions — so the tree scopes the right
        // domain's subtree to the SHA.
        watch(
            () => {
                const s = historicalEntityScope();
                return s ? `${s.entityType}:${s.entityId}:${s.revision}` : null;
            },
            () => {
                if (stek.value) {
                    sync();
                }
            }
        );

        // Refetch the worktree whenever any entity it contains changes — keyed
        // by entity type, not a per-attribute whitelist, so relationships,
        // ordering, etc. no longer go stale until a full reload.
        onEntityChange(
            (change) => WORKTREE_ENTITY_TYPES.has(change.entityType),
            () => {
                if (stek.value) {
                    sync();
                }
            }
        );

        // Same, for changes synced by other tabs (the bus above only sees this
        // tab's syncs).
        syncChannel.addEventListener(
            "message",
            ({ data: { message, identity } }) => {
                if (
                    message === "synced" &&
                    WORKTREE_ENTITY_TYPES.has(identity.entityType)
                ) {
                    if (stek.value) {
                        sync();
                    }
                }
            }
        );
    });

    // The diff status (added / modified / removed) of an entity at the currently
    // viewed revision, or undefined when unchanged / not viewing a thread branch.
    // The backend only stamps diff_status on the worktree for thread revisions.
    const diffStatusForEntity = (
        identity: string
    ): "added" | "modified" | "removed" | null | undefined => {
        const entity =
            findDomainByIdentity(identity) ??
            findProductByIdentity(identity) ??
            findProcessByIdentity(identity);
        return entity?.diff_status;
    };

    return {
        worktree,
        sync,
        isFetching,
        findDomainByIdentity,
        findProductByIdentity,
        findProcessByIdentity,
        diffStatusForEntity,
    };
});

export const useWorktreeManager = () => storeToRefs(useWorktreeStore());

export type BusinessEntityOption = {
    identity: string;
    name: string;
};

export type DomainWithBusinessEntities = {
    identity: string;
    business_entities?: BusinessEntityOption[] | null;
};

/** All business entities exposed by the worktree's domains. */
export const getLinkableBusinessEntities = (
    domains: DomainWithBusinessEntities[]
): BusinessEntityOption[] =>
    domains.flatMap((domain) => domain.business_entities ?? []);

/** Disabled-state + tooltip for "link business entity" actions. */
export const getLinkBusinessEntityButtonState = (
    domains: DomainWithBusinessEntities[]
) => {
    const availableBusinessEntities = getLinkableBusinessEntities(domains);
    return {
        disabled: availableBusinessEntities.length === 0,
        tooltip:
            availableBusinessEntities.length > 0
                ? "Link to a domain object"
                : "No domain objects available in this stek",
    };
};
